import { InputGroup, Label } from '@slal/ui/forms';
import { WarningIcon } from '@slal/ui/icons';
import { TBody, TH, THead, TR } from '@slal/ui/layout';
import { FormProvider, useFieldArray, yupResolver } from '@slal/ui/react-hook-form/core';
import { TextField } from '@slal/ui/react-hook-form/fields';
import { normalizeMonetaryAmount } from '@slal/ui/react-hook-form/utils';
import { theme } from '@slal/ui/theme';
import { Heading, P } from '@slal/ui/typography';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { LinkButton, Notice, StyledForm, ValidationWarning } from '~/components';
import { useApplicationForm } from '~/hooks';
import { fundAllocationValidationSchema } from '~/schemas';
import { ApplicationFormKeys, FundAllocation, FundAllocationsSchema, UseFundSelectorResponse } from '~/types';
import { FundAllocationProps, FundAllocationsProps } from '../../funds/types';
import { ApplicationButtons } from '../ApplicationButtons';
import { CashLikeFundWarning } from './CashLikeFundWarning';
import { TargetFundAllocation } from './TargetFundAllocation';
import { StyledFundAllocatinTableWrapper, StyledTD, StyledTFoot, StyledTable } from './styles';

export const FundAllocations = ({ fields, ...otherProps }: FundAllocationsProps) => {
    return (
        <>
            {fields?.map((field, index) => (
                <FundAllocationTR key={field.id} index={index} {...(field as FundAllocationProps)} {...otherProps} />
            ))}
        </>
    );
};

export const FundAllocationTR = ({ handleSelect, index, ...field }: FundAllocationProps & { index: number }) => {
    const { fundName, id } = field;
    const labelId = `fund-allocation-label-${id}`;

    const name = `${ApplicationFormKeys.FUND_ALLOCATIONS}.${index}.allocation`;

    const fundTitle = field.cashLikeFund ? (
        <>
            <WarningIcon color={theme.colors.warning} /> {fundName}
        </>
    ) : (
        fundName
    );

    return (
        <TR aria-labelledby={labelId}>
            <StyledTD id={labelId} aria-label={fundName}>
                {fundTitle}
            </StyledTD>
            <StyledTD>
                <InputGroup>
                    <TextField
                        name={name}
                        id={name}
                        setValueAs={(value, previousValue) => {
                            const newValue = normalizeMonetaryAmount(value, previousValue);
                            return newValue || '';
                        }}
                    />
                    <Label aria-label={`Fund percentage for ${fundName}`} htmlFor={name}>
                        %
                    </Label>
                </InputGroup>
            </StyledTD>
            <StyledTD>
                <LinkButton onClick={() => handleSelect(field)}> Remove</LinkButton>
            </StyledTD>
        </TR>
    );
};

export const FundAllocationForm = ({ fundAllocations, handleSelect }: UseFundSelectorResponse) => {
    const { form, handleSubmit } = useApplicationForm<FundAllocationsSchema>(
        {
            defaultValues: { [ApplicationFormKeys.FUND_ALLOCATIONS]: fundAllocations },
            mode: 'onSubmit',
            resolver: yupResolver(fundAllocationValidationSchema),
        },
        ApplicationFormKeys.FUND_ALLOCATIONS
    );
    const navigate = useNavigate();
    const onValidCallback = () => navigate('/application/key-considerations/information');

    const { fields, update } = useFieldArray<Pick<FundAllocationsSchema, ApplicationFormKeys.FUND_ALLOCATIONS>>({
        name: ApplicationFormKeys.FUND_ALLOCATIONS,
        control: form.control,
    });

    useEffect(() => {
        if (!fundAllocations?.length) return;
        const formValues: FundAllocation[] = form.getValues(`fundAllocations`);
        const fundCodes = new Set(fundAllocations.map(({ fundCode }) => fundCode));

        const updatedFormValues = formValues.filter(({ fundCode }) => fundCodes.has(fundCode));

        if (updatedFormValues.length !== formValues.length) {
            form.setValue(ApplicationFormKeys.FUND_ALLOCATIONS, updatedFormValues);
        }

        fundAllocations.forEach((fund, index) => {
            const existingFund = formValues.find(({ fundCode }) => fundCode === fund.fundCode);
            update(index, { ...fund, allocation: existingFund?.allocation || '' });
        });
    }, [form, fundAllocations, update]);

    return (
        <FormProvider {...form}>
            <StyledForm onSubmit={handleSubmit(onValidCallback)}>
                <Heading level={3}>Selected funds</Heading>
                {isEmpty(fundAllocations) ? (
                    <Notice>
                        <P>You do not have any funds selected</P>
                    </Notice>
                ) : (
                    <StyledFundAllocatinTableWrapper>
                        <StyledTable aria-label="Fund allocations" striped={false}>
                            <THead>
                                <TR>
                                    <TH scope="col">Fund name</TH>
                                    <TH scope="col" colSpan={2}>
                                        Fund percentage
                                    </TH>
                                </TR>
                            </THead>
                            <TBody>
                                <FundAllocations fields={fields} handleSelect={handleSelect} />
                            </TBody>
                            <StyledTFoot>
                                <TR>
                                    <StyledTD colSpan={2}>
                                        <TargetFundAllocation />
                                    </StyledTD>
                                </TR>
                            </StyledTFoot>
                        </StyledTable>
                    </StyledFundAllocatinTableWrapper>
                )}
                <CashLikeFundWarning fundAllocations={fundAllocations} />
                <ValidationWarning />
                <ApplicationButtons backTo="/application/investment-options" />
            </StyledForm>
        </FormProvider>
    );
};
